import type { HubSession } from '@ui/components/features/hub';
import { formatStreetAndCity } from '@ui/components/features/locationLibrary/format';
import { ApiError } from '@website/lib/api/apiError';
import type { ProfessionalStatus } from '@website/services/professionals.service';

import { bookingFlowHref } from './bookingFlowUrl';
import { countryName } from './country';
import { type UserColor, colorAtPosition } from './eventColor';

interface HubAddressParts {
  addressLine1: string;
  addressLine2?: string | null;
  zipCode?: string | null;
  city: string;
  country?: string | null;
}

/**
 * A Hub's address in two parts, which a narrow screen breaks between: the street with its details
 * ("12 rue X, Bâtiment B"), then the postcode, city and country (its name, not its code).
 */
export function hubAddressParts(address: HubAddressParts, locale: string): { street: string; locality: string } {
  const country = address.country ? countryName(address.country, locale) : null;
  return {
    street: formatStreetAndCity(
      { addressLine1: address.addressLine1, addressLine2: address.addressLine2 },
      { details: true }
    ),
    locality: formatStreetAndCity({ zipCode: address.zipCode, city: address.city, country }, { country: true })
  };
}

/** A Hub's address on one line: street, details, postcode and city, country (its name, not its code). */
export function formatHubFullAddress(address: HubAddressParts, locale: string): string {
  const { street, locality } = hubAddressParts(address, locale);
  return [street, locality].filter(Boolean).join(', ');
}

/**
 * Each professional's colour at a Hub, the same on its page and on its printed week:
 * Residents take the palette in a fixed order whatever the week shows; anyone else
 * with a session there comes after them.
 */
export function hubColors(
  residents: { id: string }[],
  sessions: { professionalId: string }[]
): (professionalId: string) => UserColor {
  const residentIds = [...new Set(residents.map((resident) => resident.id))].sort();
  const others = [...new Set(sessions.map((session) => session.professionalId))]
    .filter((id) => !residentIds.includes(id))
    .sort();
  const order = [...residentIds, ...others];
  return (professionalId) => colorAtPosition(professionalId, order.indexOf(professionalId));
}

/** Where a professional stands to place an activity at a Hub (see {@link placementStanding}). */
export type PlacementStanding = 'offer' | 'underReview' | 'notApproved' | 'blocked';

/**
 * Where a professional stands to place an activity at a Hub, the client side of the server's
 * placement rule: `offer` for an approved profile the Hub's Managers did not block; `underReview`
 * (`pending`) or `notApproved` for any other profile, judged before the block; `blocked`. `exempt`
 * (an admin, not judged by their own profile) skips the profile. Null while the block is not known,
 * and for someone without a professional profile (`none`); with the profile not known, only a
 * known block answers.
 */
export function placementStanding({
  profileStatus,
  blocked,
  exempt = false
}: {
  profileStatus: ProfessionalStatus | 'none' | undefined;
  blocked: boolean | undefined;
  exempt?: boolean;
}): PlacementStanding | null {
  if (!exempt && profileStatus !== 'approved') {
    if (profileStatus === undefined) return blocked ? 'blocked' : null;
    if (profileStatus === 'none') return null;
    return profileStatus === 'pending' ? 'underReview' : 'notApproved';
  }
  if (blocked === undefined) return null;
  return blocked ? 'blocked' : 'offer';
}

/** Each refusal of a write built on an older version, with its status. */
const STALE_HUB_WRITES: Record<'hub_changed' | 'revision_changed' | 'no_pending_revision', number> = {
  hub_changed: 409,
  revision_changed: 409,
  no_pending_revision: 404
};

const isStaleHubCode = (code: string): code is keyof typeof STALE_HUB_WRITES =>
  Object.prototype.hasOwnProperty.call(STALE_HUB_WRITES, code);

/**
 * A Hub save or decision refused because it was built on an older version of the Hub or of its
 * pending changes, or because those changes were decided in the meantime.
 */
export function isStaleHubWrite(error: unknown): boolean {
  return (
    error instanceof ApiError &&
    !!error.code &&
    isStaleHubCode(error.code) &&
    STALE_HUB_WRITES[error.code] === error.statusCode
  );
}

/**
 * The activity's slot picker on the session's day at the Hub location, in the booking flow's own URL
 * parameters (`useBookingUrlState`). A fixed session that is still open is restored as the selection;
 * an open window, or a full session, only opens its day.
 */
export function getHubSessionHref(
  session: Pick<
    HubSession,
    'activityId' | 'slotId' | 'date' | 'locationId' | 'startTime' | 'endTime' | 'slotType' | 'status'
  >,
  locale: string
) {
  const day = { date: session.date, locationId: session.locationId };
  if (session.slotType === 'RANGE' || session.status !== 'available')
    return bookingFlowHref(session.activityId, day, locale);
  return bookingFlowHref(
    session.activityId,
    {
      ...day,
      slotId: session.slotId,
      startTime: session.startTime,
      endTime: session.endTime,
      bookingType: session.slotType,
      locationKind: 'venue'
    },
    locale
  );
}
